/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

$(document).ready(function () {
    // $("img.lazyload").lazyload();

    var lowest_order = 999;

    $('span[data-col]:visible').each(function (index) {
        var order = $(this).css('order')
        if (order < lowest_order) {
            lowest_order = order;
        }
    })

    $('span[data-col]:visible').each(function (index) {
        var order = $(this).css('order')
        if (order == lowest_order) {
        } else {
            $(this).hide();
        }
    })


});
$(window).on('load', function () {
    var optionSelectors = $(document).find(".single-option-selector");
    var variant_selector = $(document).find("[name='id']");
    var value;
    optionSelectors.each(function () {
        var singleOptionSelector = $(this);
        singleOptionSelector.on("change", function () {
            var values = [];
            var variant = "";
            value = getOptionValues(optionSelectors, values);
            for (var i = 0; i < value.length; i++) {
                if (i == 0) {
                    variant = value[i];
                } else {
                    variant = variant + " / " + value[i];
                }
            }
            changeVariant(variant, variant_selector);
            document.dispatchEvent(
                new CustomEvent('custom-variant:changed', {
                    bubbles: true,
                    detail: { variant: variant }
                })
            );
        })
    });
    function getOptionValues(optionSelectors, values) {
        $.each(optionSelectors, function () {
            values.push($(this).find('option:selected').val());
        });
        return values;
    }
    function changeVariant(variant, variant_selector) {
        variant_selector.find('option').each(function () {
            text = $(this).text().trim();
            var baseItems = text.split('+');
            var variantItems = variant.split('+');
            if (text.indexOf(variant) > -1 && baseItems.length === variantItems.length) {
                value = $(this).val();
            }
        });
        if (typeof (value) != 'undefined') {
            variant_selector.trigger('change').val(value);
        }
    }
});

function arrayEquals(a, b) {
    var coincidences = 0;
    if (!(Array.isArray(a) && Array.isArray(b) && a.length === b.length)) {
        return false;
    }
    a.forEach(function (itemA) {
        b.forEach(function (itemB) {
            if (itemA.toLowerCase().trim() ===
                itemB.toLowerCase().trim()) {
                coincidences++;
            }
        })
    });
    return (coincidences === a.length) ? true : false;
}

function CountdownTimer() {

    this.initialize = function (element) {
        this.element = element;
        this._refs = {
            days: this.element.querySelector('[data-value="days"]'),
            hours: this.element.querySelector('[data-value="hours"]'),
            mins: this.element.querySelector('[data-value="minutes"]'),
            secs: this.element.querySelector('[data-value="seconds"]'),
        };
    }

    this.setTargetDate = function (dateString) {
        var dateArray = dateString.split('-');
        var months = dateArray[1] - 1;
        var days = dateArray[0];
        var fullYear = dateArray[2];
        var hours = dateArray[3];
        return new Date(Date.UTC(fullYear, months, days, hours));
    }

    this._getTimeRemaining = function (endtime) {

        var total = Date.parse(endtime) - Date.parse(new Date().toUTCString());
        var error = (total < 1) ? true : false;
        var days = Math.floor(total / (1000 * 60 * 60 * 24));
        var hours = Math.floor((total / (1000 * 60 * 60)) % 24);
        var mins = Math.floor((total / 1000 / 60) % 60);
        var secs = Math.floor((total / 1000) % 60);
        return {
            total,
            days,
            hours,
            mins,
            secs,
            error,
        };
    }

    this._updateTimer = function ({ days, hours, mins, secs }) {
        this._refs.days.textContent = days;
        this._refs.hours.textContent = hours;
        this._refs.mins.textContent = mins;
        this._refs.secs.textContent = secs;
    }

    this.startTimer = function (targetDate) {
        if (!targetDate) {
            this.element.style.display = 'none'
            return
        }
        this.targetDate = targetDate;
        var timer = this._getTimeRemaining(this.targetDate);
        if (timer.error) {
            this.element.style.display = 'none'
            return;
        }
        this._updateTimer(timer);
        invertvalID = setInterval(() => {
            var timer = this._getTimeRemaining(this.targetDate);
            if (timer.error) {
                this.element.style.display = 'none';
                clearInterval(invertvalID);
            }
            this._updateTimer(timer);
        }, 1000);
    }
}

//   Tidal Rise - Announcement Bar Countdown Timer
(function () {

    var timerElement = document.querySelector('[data-promotion-timer]');
    if (!timerElement) {
        return
    }

    window.countdownTimer = window.countdownTimer || new CountdownTimer();

    window.countdownTimer.initialize(timerElement);
    var targetDateString = timerElement.dataset.promotionTimer;
    var targetDate = window.countdownTimer.setTargetDate(targetDateString);
    window.countdownTimer.startTimer(targetDate);
})();

(function MoveToNextSectionArrow() {
    var btn = document.querySelector('.move-to-section')
    if (!btn) {
        return;
    }

    var currentSection = btn.closest('[class^="shopify-section"]')
    var nextSection = currentSection.nextSibling;

    btn.addEventListener('click', function () {
        nextSection.scrollIntoView({ behavior: 'smooth' });
    })
})();

class CurrencySelectorForm extends HTMLElement {
    constructor() {
        super();
        this.refs = {
            input: this.querySelector('input[name="country_code"]'),
            button: this.querySelector('button'),
            menuList: this.querySelector('ul'),
        };
        this.refs.button.addEventListener('click', this.openSelector.bind(this));
        this.refs.button.addEventListener('focusout', this.closeSelector.bind(this));
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

        this.querySelectorAll('a').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
    }

    hidePanel() {
        this.refs.button.setAttribute('aria-expanded', 'false');
        this.refs.menuList.setAttribute('hidden', true);
    }

    onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;

        this.hidePanel();
        this.refs.button.focus();
    }

    onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.refs.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
    }

    openSelector() {
        this.refs.button.focus();
        this.refs.menuList.toggleAttribute('hidden');
        this.refs.button.setAttribute('aria-expanded', (this.refs.button.getAttribute('aria-expanded') === 'false').toString());
    }

    closeSelector(event) {
        const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
        if (event.relatedTarget === null || shouldClose) {
            this.hidePanel();
        }
    }
}
customElements.define('currency-selector', CurrencySelectorForm);

class PriceRange extends HTMLElement {

    constructor() {
        super();
        var _this = this;

        this.rangeLowerBound = this.querySelector('.price-range__range-group input:first-child');
        this.rangeHigherBound = this.querySelector('.price-range__range-group input:last-child');
        this.textInputLowerBound = this.querySelector('.price-range__input:first-child input');
        this.textInputHigherBound = this.querySelector('.price-range__input:last-child input'); // Select whole text on focus for text field to improve user experience

        this.textInputLowerBound.addEventListener('focus', function () {
            return _this.textInputLowerBound.select();
        });
        this.textInputHigherBound.addEventListener('focus', function () {
            return _this.textInputHigherBound.select();
        }); // Keep in sync the range with the text input fields

        this.textInputLowerBound.addEventListener('change', function (event) {
            event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(_this.textInputHigherBound.value || event.target.max) - 1), event.target.min);
            _this.rangeLowerBound.value = event.target.value;

            _this.rangeLowerBound.parentElement.style.setProperty('--range-min', "".concat(parseInt(_this.rangeLowerBound.value) / parseInt(_this.rangeLowerBound.max) * 100, "%"));
        });
        this.textInputHigherBound.addEventListener('change', function (event) {
            event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(_this.textInputLowerBound.value || event.target.min) + 1), event.target.max);
            _this.rangeHigherBound.value = event.target.value;

            _this.rangeHigherBound.parentElement.style.setProperty('--range-max', "".concat(parseInt(_this.rangeHigherBound.value) / parseInt(_this.rangeHigherBound.max) * 100, "%"));
        });
        this.rangeLowerBound.addEventListener('change', function (event) {
            _this.textInputLowerBound.value = event.target.value;

            _this.textInputLowerBound.dispatchEvent(new Event('change', {
                bubbles: true
            }));
        });
        this.rangeHigherBound.addEventListener('change', function (event) {
            _this.textInputHigherBound.value = event.target.value;

            _this.textInputHigherBound.dispatchEvent(new Event('change', {
                bubbles: true
            }));
        }); // We also have to bound the two range sliders

        this.rangeLowerBound.addEventListener('input', function (event) {
            event.target.value = Math.min(parseInt(event.target.value), parseInt(_this.textInputHigherBound.value || event.target.max) - 1); // Bound the value

            event.target.parentElement.style.setProperty('--range-min', "".concat(parseInt(event.target.value) / parseInt(event.target.max) * 100, "%"));
            _this.textInputLowerBound.value = event.target.value;
        });
        this.rangeHigherBound.addEventListener('input', function (event) {
            event.target.value = Math.max(parseInt(event.target.value), parseInt(_this.textInputLowerBound.value || event.target.min) + 1); // Bound the value

            event.target.parentElement.style.setProperty('--range-max', "".concat(parseInt(event.target.value) / parseInt(event.target.max) * 100, "%"));
            _this.textInputHigherBound.value = event.target.value;
        });
    }
}
window.customElements.define('price-range', PriceRange);




// Custom filters

function ProductsRevealer(){

    var _this = this;

    this.selectors = {
        flag: '[data-load-more]',
        productsList: '[data-infinite-scrolling]',
        productItem: '.ProductItem',
    }

    this.newPage = null;
    this.loading = false;   
    this.pageNumber = 1;

    this.baseURL = window.location.protocol + '//' + window.location.hostname;

    this.initialize = function(el){
        this.wrapper = el;
        this.button = document.querySelector(this.selectors.flag);
        if(!this.button){
            return;
        }
        this.fragment = new DocumentFragment();
        this.domParser = new DOMParser();
        this.spinner = new LoadingSpinner();
        this.observer = new IntersectionObserver(this.handleIntersection.bind(this), { rootMargin: '0px 0px 200px 0px' });
        location.hash = location.hash || '#page=' + this.pageNumber;
        this._attachListeners();
    }

    this._attachListeners = function(){
        this.button.addEventListener('click', this._handleReveal.bind(this));
        document.addEventListener('collection:reloaded', this._handleProductsFiltered.bind(this));
        document.addEventListener('products:fetch', this._handlePageRestore.bind(this));
        this.observer.observe(this.button);
    }

    this.waitUntilLoaded = function() {
        return new Promise(function(resolve) {
            const listener = function() {
                _this.wrapper.removeEventListener('products:fetched', listener);
                resolve();
            }
            _this.wrapper.addEventListener('products:fetched', listener);
        })
    }

    this._handleProductsFiltered = function (e) {
        document.dispatchEvent(new Event('pagination:reset'));
        var newFlag = e.detail.newFlag
        if (!newFlag) {
            return;
        }
        var currentFlag = document.querySelector(this.selectors.flag);
        currentFlag.addEventListener('click', this._handleReveal.bind(this));
        setTimeout(function () {
            _this.observer.observe(currentFlag);
        }, 200)
    }

    this.handleIntersection = function (entries, observer) {
        var entry = entries[0];
        if (entry.isIntersecting) {
            this.loading = true;    
            observer.unobserve(entry.target);
            var url = this.baseURL + entry.target.getAttribute('href');
            this.fetchPage(url).then(function (page) {
               _this.newPage = page;
                _this.loading = false;
                _this.wrapper.dispatchEvent(new CustomEvent('products:fetched'));  
            });
        }
    }

    this._handleReveal = function(e){
        e.preventDefault();
        this.spinner.show(this.wrapper);    
        if (this.loading) {
            this.waitUntilLoaded(this.wrapper).then(function(){
                this._revealProducts();
            }.bind(this));
        }else{
            this._revealProducts();
        }
        
    }

    this._handlePageRestore = function(e){
        var pages = e.detail.pages;
        this.fetchMultiplePages(pages).then(function(results){
            results.forEach(function(newPage,index){
                var products = newPage.querySelectorAll(_this.selectors.productItem);
                products.forEach(function (product) {
                    _this.fragment.append(product.parentElement)
                });                
                
                var newButton = newPage.querySelector(_this.selectors.flag);
                if (newButton) {
                    _this.button.setAttribute("href", newButton.getAttribute('href'));
                    _this.button.classList.remove('disabled');
                    if (index === results.length - 1) {
                        _this.observer.observe(_this.button)
                    }
                } else {
                    _this.button.remove();
                }
            })
            _this.wrapper.append(_this.fragment);
            _this.fragment.replaceChildren();

        }).catch(function(err) {
            console.log(err);
        });
    }

    this.fetchMultiplePages = function(pages){
        var promises = [];
        var url = new URL(this.baseURL + _this.button.getAttribute('href'));
        for (var i = 0; i < pages; i++) { 
            url.searchParams.set('page', i+2);                     
            promises.push(this.fetchPage(url.toString()));
        }
        return Promise.all(promises);
    }



    this._revealProducts = function(e){
        document.dispatchEvent(new Event('pagination:updated'));
        this.wrapper = document.querySelector(this.selectors.productsList);
        this.button = document.querySelector(this.selectors.flag);
        this.button.classList.add('disabled');

        var products = this.newPage.querySelectorAll(this.selectors.productItem);
        products.forEach(function (product) {       
            _this.fragment.append(product.parentElement)
        });
        this.wrapper.append(_this.fragment);
        this.fragment.replaceChildren();
        var newButton = this.newPage.querySelector(_this.selectors.flag);        
        if (newButton) {
            this.button.setAttribute("href", newButton.getAttribute('href'));
            this.button.classList.remove('disabled');
            this.observer.observe(this.button)
        } else {
            this.button.remove();
        }
        this.spinner.remove();
    }

    this.fetchPage = function(url){
        return fetch(url).then(function (response) {
            return response.text();
        }).then(function (data) {
            return _this.domParser.parseFromString(data, "text/html");
        }).catch(function(e){
            console.e('Error loading more products. Details :')
            console.e(e)
        })
    }
}

function LoadingSpinner(){
    this.el  = document.createElement('div');
    this.el.classList.add('spinner', 'infinite-scroll-loading');

    this.show = function(wrapper){
        wrapper.after(this.el);
    }
    this.remove = function () {
        this.el.remove();
    }
}

function URLPaginator(base){

    this.base = base + '=';
    this.number = 1;

    this.initialize = function(){
        this.number = this.getHashNumberStored();
        this.updateHash();
        document.addEventListener('pagination:updated',this.updateURL.bind(this));
        document.addEventListener('pagination:set',this.setURL.bind(this));
        document.addEventListener('pagination:reset',this.updateURL.bind(this));

    }

    this.getHashNumberStored = function(){
        var previousHash = window.sessionStorage.getItem('pageHash');    
        return (previousHash) ? this.getHashNumber(previousHash) : this.number;
    }

    this.setHashNumberStorage = function(){
        window.sessionStorage.setItem('pageHash',this.base + this.number);
    }

    this.getHashNumber = function(hash){
        var splitted = hash.split('=');
        var hash = parseInt(splitted[splitted.length - 1]);
        return hash;
    }

    this.setURL = function (e) {
        this.number = e.detail.page;
        this.updateHash();
        document.dispatchEvent(new CustomEvent('products:fetch',{
            detail: {
                pages: this.number - 1
            }
        }));
    }

    this.updateURL = function(e){
        console.log('Event type',e.type);
        this.number = e.type == 'pagination:reset' ? 1 : this.number + 1;
        this.updateHash();
    }

    this.updateHash = function(){
        window.location.hash = this.base + this.number;
    }

    this.getHash = function(){
        return window.location.hash;
    }
}

function TrailKeeper() {

    this.settings = {
        statuses: Object.freeze({
            initial: 'initial',
            product: 'product',
            last: 'last'
        }),
    }
    this.initialize = function (template) {
        if (['collection','search'].includes(template)) {
            document.addEventListener('pagination:updated', this.updateTrail.bind(this));
            document.addEventListener('pagination:reset', this.resetPageTrail.bind(this));
            if (this.isLastTrail(template)) {
                this.executeLastTrail();
            } else {
                this.setFirstTrail();
            }

        } else if (template == 'product') {
            if (this.hasTrail()) {
                this.updateTrailWithProductData(this.getProductHandle());
            }
        } else {
            this.removeTrail();
        }
    }

    this.resetPageTrail = function(){
        var t = this.getTrail();
        if (t) {
            t.pagination = 1;
            t.url = window.location.href.replace(location.hash, "")
        }
        window.sessionStorage.setItem('trail', JSON.stringify(t));
    }

    this.updateTrail = function () {
        var t = this.getTrail();
        if (t) {
            ++t.pagination
            t.url = window.location.href.replace(location.hash, "");
        }
        window.sessionStorage.setItem('trail', JSON.stringify(t));
    }

    this.getProductHandle = function () {
        const segments = new URL(window.location.href).pathname.split('/');
        const productHandle = segments.pop() || segments.pop();
        return productHandle;
    }

    this.updateTrailWithProductData = function (product) {
        var t = this.getTrail();
        if (t) {
            t.product = product
            t.state = this.settings.statuses.product;
        }
        window.sessionStorage.setItem('trail', JSON.stringify(t));
    }


    this.executeLastTrail = function () {
        var t = this.getTrail();
        if (window.location.href.replace(window.location.hash, "") === t.url){
            document.dispatchEvent(new CustomEvent('pagination:set', {
                detail: {
                    page: t.pagination
                }
            }));
            console.log('trail', t);
        }
        this.removeTrail();
        this.setFirstTrail();
    }

    this.removeTrail = function () {
        window.sessionStorage.removeItem('trail');
    }

    this.setFirstTrail = function () {
        var trail = {
            state: this.settings.statuses.initial,
            url: window.location.href.replace(location.hash, ""),
            pagination: window.location.hash && parseInt(window.location.hash.split('=')[1])
        }
        window.sessionStorage.setItem('trail', JSON.stringify(trail));
    }

    this.isLastTrail = function (template) {
        var t = this.getTrail();
        return (t && t.state === this.settings.statuses.product && t.url === window.location.href.replace(location.hash,'')) ? true : false;
    }

    this.getTrail = function () {
        return JSON.parse(window.sessionStorage.getItem('trail'));
    }
    this.hasTrail = function () {
        return this.getTrail() ? true : false;
    }
}

(function main(){

    var wrapper = document.querySelector('[data-infinite-scrolling]');
    if (wrapper) {
        var pr = new ProductsRevealer();
        pr.initialize(wrapper);
    }

    var templ = window.theme.template;    
    if (['collection', 'search'].includes(templ)) {
        var paginator = new URLPaginator('page');
        paginator.initialize();
    }
    if (['collection', 'product', 'search'].includes(templ)) {
        new TrailKeeper().initialize(window.theme.template);
    }

    
       

})();






















// Infinite Scrolling on collection page
// (function () {
// 
//     var settings = {
//         selectors: {
//             flag: '[data-load-more]',
//             productsList: '[data-infinite-scrolling]',
//         },
//         BASE_URL: window.location.protocol + '//' + window.location.hostname,
//     };
// 
//     var $listWrapper = document.querySelector(settings.selectors.productsList);
//     var initialFlag = document.querySelector(settings.selectors.flag);
// 
//     if (!$listWrapper || !initialFlag ) {
//         return;
//     }
// 
//     var loadingSpinner = document.createElement('div');
//     loadingSpinner.classList.add('spinner', 'infinite-scroll-loading');
// 
//     var fragment = new DocumentFragment();
// 
//     initialFlag.addEventListener('click',handleShowMore)
//     document.addEventListener('collection:reloaded', function (e) {
//         var newFlag = e.detail.newFlag
//         if (!newFlag){
//             return;
//         }
//         var currentFlag = document.querySelector(settings.selectors.flag);
//         currentFlag.addEventListener('click',handleShowMore);
//     });
// 
//     function handleShowMore(event) {
// 
//         event.preventDefault();
//         var url = settings.BASE_URL + event.target.getAttribute('href')
// 
//         var $listWrapper = document.querySelector(settings.selectors.productsList);
//         $listWrapper.after(loadingSpinner)
//         event.target.classList.add('disabled');
// 
//         fetch(url).then(function (response) {
//             return response.text();
//         }).then(function (data) {
//             var $html = new DOMParser().parseFromString(data, "text/html");
//             var products = $html.querySelectorAll('.ProductItem');
//             products.forEach(function (product) {
//                 fragment.append(product.parentElement)
//             })
//             loadingSpinner.remove();
//             $listWrapper.append(fragment);
//             fragment.replaceChildren();
// 
//             var newFlag = $html.querySelector(settings.selectors.flag);
//             if(newFlag){
//                 event.target.setAttribute("href", newFlag.getAttribute('href'));
//                 event.target.classList.remove('disabled')
//             }else{
//                 event.target.remove();
//             }
//         })
//     }
// 
// })(); 
// Infinite Options by ShopPad
// This theme removes event handlers attached to the add to cart button when the variant changes
// Reinit Infinite Options to reattach event handlers
document.addEventListener('variant:changed', function(event) {
  window.Shoppad.apps.infiniteoptions.init();
});


/* shimaa cart edit for bundle */
document.addEventListener('DOMContentLoaded', () => {
  // Toggle logic for toggal_bundle_content buttons
  const attachToggleListeners = () => {
    document.querySelectorAll('.toggal_bundle_content').forEach(button => {
      button.removeEventListener('click', toggleHandler); // Prevent duplicate listeners
      button.addEventListener('click', toggleHandler);
    });
  };

  const toggleHandler = (event) => {
    const button = event.currentTarget;
    const content = button.closest('.small-text').querySelector('.bundle_content');
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', !isExpanded);
    content.style.height = isExpanded ? '0' : '100%';
    const showText = button.querySelector('.bundle_show_text');
    const hideText = button.querySelector('.bundle_hide_text');
    if (showText && hideText) {
      showText.style.display = isExpanded ? 'inline' : 'none';
      hideText.style.display = isExpanded ? 'none' : 'inline';
    }
  };

  // Quantity decrease logic for main bundle items
  const attachQuantityDecreaseListeners = () => {
    document.querySelectorAll('.bundle_main_item .quantity-down:not(.bundle-sub-item .quantity-down)').forEach(link => {
      link.removeEventListener('click', quantityDecreaseHandler); // Prevent duplicate listeners
      link.addEventListener('click', quantityDecreaseHandler);
    });
  };

  const quantityDecreaseHandler = (event) => {
    event.preventDefault();
    const link = event.currentTarget;
    const mainItemKey = link.getAttribute('href').match(/id=([^&]+)/)[1];
    const mainItemInput = link.closest('.quantity').querySelector('input.cart-item__quantity-input');
    const mainItemQuantity = parseInt(mainItemInput.value, 10);
    const mainBundleElement = link.closest('.bundle_main_item');
    const mainBundleDiscount = mainBundleElement.getAttribute('bundle_discount');
    const bundleSize = parseInt(mainBundleElement.getAttribute('bundle_quantity'), 10);

    if (!mainBundleDiscount || isNaN(bundleSize)) {
      console.error('Bundle discount or quantity not found on main item');
      return;
    }

    mainBundleElement.classList.add('merge-item-refreshing');

    // Fetch cart to get sub-items
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const updates = {};
        const mainItem = cart.items.find(item => item.key === mainItemKey);
        if (!mainItem) return;

        // Find sub-items (same discount, not main bundle)
        const subItems = cart.items.filter(item =>
          item.key !== mainItemKey &&
          item.discounts.some(discount => discount.title === mainBundleDiscount) &&
          !Object.keys(item.properties).some(key => key === '_Main_bundle')
        );

        if (mainItemQuantity === 1) {
          // Remove main item and all sub-items
          updates[mainItemKey] = 0;
          subItems.forEach(subItem => {
            updates[subItem.key] = 0;
          });
        } else {
          // Decrease main item by 1
          updates[mainItemKey] = mainItemQuantity - 1;

          // Select bundleSize sub-items to remove (prioritize zeroing out items)
          let remainingToRemove = bundleSize;
          const sortedSubItems = [...subItems].sort((a, b) => a.quantity - b.quantity); // Sort by quantity (ascending)
          for (const subItem of sortedSubItems) {
            if (remainingToRemove <= 0) {
              updates[subItem.key] = subItem.quantity; // Keep existing quantity
            } else if (subItem.quantity <= remainingToRemove) {
              updates[subItem.key] = 0; // Remove item entirely
              remainingToRemove -= subItem.quantity;
            } else {
              updates[subItem.key] = subItem.quantity - remainingToRemove; // Reduce by remaining amount
              remainingToRemove = 0;
            }
          }
        }

        // Update cart
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        })
          .then(response => response.json())
          .then(data => {
            console.log('Cart updated:', data);
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
            document.dispatchEvent(new CustomEvent('on:cart:after-merge', { bubbles: true, cancelable: false }));
          })
          .catch(error => console.error('Error updating cart:', error));
      })
      .catch(error => console.error('Error fetching cart:', error));
  };

  // Remove non-discounted main bundle items
  const removeNonDiscountedBundles = () => {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const updates = {};
        cart.items.forEach(item => {
           const hasMainBundle = Object.keys(item.properties).some(key => key === '_Main_bundle');
          const bundleDiscountTitle = item.properties?.Bundle_Discount;
          const bundleRef = item.properties?._bundle_ref || item.properties?._pack;
          const piecesAreFree = bundleRef && cart.items.some(other => other.key !== item.key && ((other.properties?._bundle_ref === bundleRef) || (other.properties?._pack === bundleRef)) && other.final_line_price === 0);
          const hasBundleDiscount = (bundleDiscountTitle && item.discounts.some(discount => discount.title === bundleDiscountTitle)) || (item.discounts && item.discounts.length > 0) || piecesAreFree;
          if (hasMainBundle && !hasBundleDiscount) {
            updates[item.key] = 0;
          }
        });
        if (Object.keys(updates).length > 0) {
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          })
            .then(response => response.json())
            .then(data => {
              console.log('Non-discounted main bundle items removed:', data);
              document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
            })
            .catch(error => console.error('Error removing items:', error));
        }
      })
      .catch(error => console.error('Error fetching cart:', error));
  };

  const standardQuantityHandler = (event) => {
    event.preventDefault();
    const link = event.currentTarget;
    const href = link.getAttribute('href');
    const urlParams = new URLSearchParams(href.split('?')[1]);
    const itemKey = urlParams.get('id');
    const newQuantity = parseInt(urlParams.get('quantity'), 10);

    if (!itemKey || isNaN(newQuantity)) {
      console.error('Invalid item key or quantity in href:', href);
      return;
    }

    const updates = { [itemKey]: newQuantity };

    // Update cart
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    })
      .then(response => response.json())
      .then(data => {
        console.log('Sub-item quantity updated:', data);
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
        document.dispatchEvent(new CustomEvent('on:cart:after-merge', { bubbles: true, cancelable: false }));
      })
      .catch(error => console.error('Error updating sub-item quantity:', error));
  };

  const attachStandardQuantityListeners = () => {
    // Target quantity buttons within sub-items (adjust selector as needed)
    document.querySelectorAll('.bundle-sub-item .quantity-down, .bundle-sub-item .quantity-up').forEach(button => {
      button.removeEventListener('click', standardQuantityHandler); // Prevent duplicate listeners
      button.addEventListener('click', standardQuantityHandler);
    });
  };

  // Initial attachment of listeners
  attachToggleListeners();
  attachQuantityDecreaseListeners();
  // Re-attach listeners and check non-discounted bundles after cart updates
  document.addEventListener('on:cart:after-merge', () => {
    attachToggleListeners();
    attachQuantityDecreaseListeners();
    removeNonDiscountedBundles();
    attachStandardQuantityListeners();
  });
});

  const autoCheckGuaranteeIfFirstItem = async () => {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      if (cart.item_count === 1) {
        const checkbox = document.querySelector('#Gurantee_product_add .add-to-cart-checkbox');
        if (checkbox && !checkbox.checked) {
          checkbox.checked = true;
          // This triggers your existing add-to-cart logic so the product actually gets added
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    } catch (e) {
      console.error('Failed to check cart count', e);
    }
  };

  // Run this every time something is added to cart
  document.addEventListener('on:cart:add', () => {
     autoCheckGuaranteeIfFirstItem();
  });
